import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Router } from '@angular/router'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'

import { form, pattern, required } from '@angular/forms/signals'

import { Kirjanpitaja, KirjanpitajanAsetukset } from '../_jaettu-lemonator/model/kirjanpitaja'

import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { KirjanpitajanAllekirjoitusService } from 'app/_jaettu-lemonator/service/kirjanpitajan-allekirjoitus.service'

import { KirjanpitoRaporttiEnsinNaytettava, KirjanpitoRaporttiValilehti } from '../_jaettu-lemonator/model/kirjanpitaja'

import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'

import { TuettuKieli } from '@lemon/core/model/common'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { firstValueFrom, Observable, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

interface AsetuksetLomake {
  phoneNumber: string
  emailEndTextFi: string
  emailEndTextEn: string
  lomaviestiActive: boolean
  lomaviestiFi: string
  lomaviestiEn: string
}

@Component({
  templateUrl: './kirjanpitajan-asetukset.component.html',
  styleUrls: ['./kirjanpitajan-asetukset.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjanpitajanAsetuksetComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)

  kirjanpitajaObservable: Observable<Kirjanpitaja>
  kirjanpitajanAsetuksetObservable: Observable<KirjanpitajanAsetukset>
  kirjanpidonRaportit: { avain: KirjanpitoRaporttiValilehti; nimi: String }[] = []
  kumpiRaporttiEnsin: { avain: KirjanpitoRaporttiEnsinNaytettava; nimi: String }[] = []

  lomake = form(signal<AsetuksetLomake>({ phoneNumber: '', emailEndTextFi: '', emailEndTextEn: '', lomaviestiActive: false, lomaviestiFi: '', lomaviestiEn: '' }), polku => {
    required(polku.phoneNumber)
    pattern(polku.phoneNumber, /^[\d ()+-]+$/)
    required(polku.emailEndTextFi)
    required(polku.emailEndTextEn)
  })

  oletusraportti = form(signal<KirjanpitoRaporttiValilehti | null>(null), polku => {
    required(polku)
  })
  ensinNaytettavaRaportti = form(signal<KirjanpitoRaporttiEnsinNaytettava | null>(null), polku => {
    required(polku)
  })

  readonly commonError = signal<string>(undefined)

  constructor(
    private _kirjanpitajaService: KirjanpitajaService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _router: Router,
    private _kirjanpitajanAllekirjoitusService: KirjanpitajanAllekirjoitusService,
    private _snackbar: MatSnackBar,
    private _firebase: FirebaseLemonator,
    private _formValidationService: FormValidationService,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
  ) {}

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  ngOnInit() {
    this.kirjanpitajaObservable = this._kirjanpitajaService.kirjautuneenKayttajanKirjanpitajaObservable
    this.kirjanpitajaObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe((accountant: Kirjanpitaja) => {
      if (accountant?.puhelin) {
        asetaArvo(this.lomake.phoneNumber, accountant.puhelin)
      }

      if (accountant.emailEndText?.['fi']) {
        asetaArvo(this.lomake.emailEndTextFi, accountant.emailEndText['fi'])
      } else {
        const defaultEmailEndTextFi = this._createDefaultEmailEndText(accountant, 'fi')
        asetaArvo(this.lomake.emailEndTextFi, defaultEmailEndTextFi)
      }

      if (accountant.emailEndText?.['en']) {
        asetaArvo(this.lomake.emailEndTextEn, accountant.emailEndText['en'])
      } else {
        const defaultEmailEndTextEn = this._createDefaultEmailEndText(accountant, 'en')
        asetaArvo(this.lomake.emailEndTextEn, defaultEmailEndTextEn)
      }

      if (accountant.lomaviesti?.['fi'] || accountant.lomaviesti?.['en']) {
        console.log(accountant.lomaviesti?.['fi'], accountant.lomaviesti?.['en'])
        asetaArvo(this.lomake.lomaviestiActive, true)
      }

      if (accountant.lomaviesti?.['fi']) {
        asetaArvo(this.lomake.lomaviestiFi, accountant.lomaviesti['fi'])
      }

      if (accountant.lomaviesti?.['en']) {
        asetaArvo(this.lomake.lomaviestiEn, accountant.lomaviesti['en'])
      }
    })

    this.kirjanpitajanAsetuksetObservable = this._kirjanpitajaService.kirjautuneenKayttajanAsetuksetObservable
    this.kirjanpitajanAsetuksetObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(asetukset => {
      asetaArvo(this.oletusraportti, asetukset?.kirjanpidonOletusraportti, { ilmoita: false })
      asetaArvo(this.ensinNaytettavaRaportti, asetukset?.kirjanpidonEnsinNaytettevaRaportti, { ilmoita: false })
    })
    arvonMuutokset(this.oletusraportti, { injector: this._injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(raportti => this.oletusRaporttiMuuttui(raportti))
    arvonMuutokset(this.ensinNaytettavaRaportti, { injector: this._injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(raportti => this.ensinNaytettavaRaporttiMuuttui(raportti))

    this.kirjanpidonRaportit.push({ avain: 'tase-ja-tulos', nimi: 'Tase ja tulos' })
    this.kirjanpidonRaportit.push({ avain: 'viralliset', nimi: 'Virallinen tase ja tulos' })
    this.kirjanpidonRaportit.push({ avain: 'paakirja', nimi: 'Pääkirja' })

    this.kumpiRaporttiEnsin.push({ avain: 'tase', nimi: 'tase ensin' })
    this.kumpiRaporttiEnsin.push({ avain: 'tulos', nimi: 'tulos ensin' })
  }

  async oletusRaporttiMuuttui(event: KirjanpitoRaporttiValilehti) {
    const kirjanpitajanTiedot = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable)
    if (kirjanpitajanTiedot) {
      this._firebase
        .firestoreSetData<Partial<KirjanpitajanAsetukset>>(
          '/kirjanpitajat/' + kirjanpitajanTiedot.uid + '/kirjanpitajan-asetukset/' + kirjanpitajanTiedot.uid,
          { kirjanpidonOletusraportti: event },
          { merge: true },
        )
        .then(() => {
          this._snackbar.open('Oletusraportti tallennettiin', 'OK', { verticalPosition: 'top', duration: 1500 })
        })
    }
  }

  async ensinNaytettavaRaporttiMuuttui(event: KirjanpitoRaporttiEnsinNaytettava) {
    const kirjanpitajanTiedot = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable)
    if (kirjanpitajanTiedot) {
      this._firebase
        .firestoreSetData<Partial<KirjanpitajanAsetukset>>(
          '/kirjanpitajat/' + kirjanpitajanTiedot.uid + '/kirjanpitajan-asetukset/' + kirjanpitajanTiedot.uid,
          { kirjanpidonEnsinNaytettevaRaportti: event },
          { merge: true },
        )
        .then(() => {
          this._snackbar.open('Ensin näytettävä raportti tallennettiin', 'OK', { verticalPosition: 'top', duration: 1500 })
        })
    }
  }

  async saveForm() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }
    this.commonError.set(null)
    this._ladataanService.aloitaLataaminen()

    try {
      const phoneNumber = this.lomake.phoneNumber().value()
      const emailEndTextFi = this.lomake.emailEndTextFi().value()
      const emailEndTextEn = this.lomake.emailEndTextEn().value()
      const lomaviestiActive = this.lomake.lomaviestiActive().value()
      const lomaviestiFi = lomaviestiActive ? this.lomake.lomaviestiFi().value() : null // Overwrite lomaviesti text if not active
      const lomaviestiEn = lomaviestiActive ? this.lomake.lomaviestiEn().value() : null // Overwrite lomaviesti text if not active

      const kirjanpitajanTiedot = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable)
      if (!kirjanpitajanTiedot || !phoneNumber) {
        return
      }
      const updateData: Pick<Kirjanpitaja, 'puhelin' | 'emailEndText' | 'lomaviesti'> = {
        puhelin: phoneNumber,
      }

      if (!updateData.emailEndText) {
        updateData.emailEndText = {} as any
      }
      updateData.emailEndText['fi'] = emailEndTextFi
      updateData.emailEndText['en'] = emailEndTextEn

      if (!updateData.lomaviesti) {
        updateData.lomaviesti = {} as any
      }
      updateData.lomaviesti['fi'] = lomaviestiFi
      updateData.lomaviesti['en'] = lomaviestiEn

      await this._firebase.firestoreUpdateData<Partial<Kirjanpitaja>>('kirjanpitajat/' + kirjanpitajanTiedot.uid, updateData)

      this._snackbar.open('Tiedot tallennettu', 'OK', { verticalPosition: 'top', duration: 1500 })
    } catch (error) {
      this._errorHandler.handleError(error)
      this.commonError.set('Tallentamisen aikana tapahtui virhe. Ole hyvä ja ilmoita tästä ylläpitäjälle.')
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  back() {
    this._router.navigate(['/'])
  }

  async copySignature(kirjanpitaja: Kirjanpitaja, kieli: TuettuKieli) {
    const signature = await this._kirjanpitajanAllekirjoitusService.getSignatureHtml(kirjanpitaja, kieli)
    this._copyToClipboard(signature)
    this._snackbar.open('Allekirjoituksesi on tallennettu leikepöydälle. Liitä (Cmd+V) se sähköpostin allekirjoituksien muokkauskentälle.', 'OK', {
      verticalPosition: 'top',
      duration: 1500,
    })
  }

  private _copyToClipboard(html: string) {
    // TODO: Find way to use already rendered signature
    const container = document.createElement('div')
    container.innerHTML = html
    container.style.position = 'fixed'
    container.style.pointerEvents = 'none'
    container.style.opacity = '0'
    document.body.appendChild(container)
    window.getSelection().removeAllRanges()
    const range = document.createRange()
    range.selectNode(container)
    window.getSelection().addRange(range)
    document.execCommand('copy')
    document.body.removeChild(container)
  }

  private _createDefaultEmailEndText(accountant: Kirjanpitaja, lang: TuettuKieli) {
    if (lang === 'fi') {
      return 'Ystävällisin terveisin,\n\n' + accountant.etunimi
    } else if (lang === 'en') {
      return 'Kind regards,\n\n' + accountant.etunimi
    } else {
      throw new Error('No email text set up for ' + lang)
    }
  }
}
